import { photos } from "@/platform"
import type { RestoredCapture } from "@/platform"

import { stagedPhotoToDraft } from "./draftPhotos"
import { findDraftAwaitingCapture, saveDraftSnapshot } from "./draftsDb"

/**
 * How long a form will wait at startup for a restored capture to be delivered
 * before mounting anyway. Long enough for the bridge to hand over an event it
 * is already holding, short enough that a normal launch — where there is
 * nothing to deliver — is not visibly slower.
 */
const RECOVERY_GRACE_MS = 1_500

let settled: Promise<void> | null = null
let markSettled: (() => void) | null = null

/**
 * Reattaches a capture the OS held for us because our process died while the
 * camera was in front, to whichever draft was waiting on it.
 *
 * Every outcome clears `captureInFlight`, not just a successful one. The
 * JavaScript `finally` that would normally have cleared it died with the
 * process, so a cancelled capture left alone would have the form come back
 * later claiming a photo was lost when the user had simply changed their mind.
 */
export type RecoveryOutcome =
  /** A photo was reattached to the waiting draft and durably stored. */
  | "recovered"
  /** Nothing to reattach: no waiting draft, or the capture was cancelled. */
  | "nothing-to-do"
  /** A photo came back but could not be stored. The draft has not got it. */
  | "failed"

async function reattach(capture: RestoredCapture): Promise<RecoveryOutcome> {
  const draft = await findDraftAwaitingCapture()
  if (!draft) return "nothing-to-do"

  let photo = null
  if (capture.status === "captured") {
    try {
      photo = await stagedPhotoToDraft({
        id: crypto.randomUUID(),
        caption: "",
        file: await capture.file,
      })
    } catch (error) {
      console.warn("[Recovery] Could not read the restored capture", error)
    }
  }

  // Clearing the flag matters even when there is no photo — whatever was
  // waiting on the capture has to stop waiting either way.
  const { saved } = await saveDraftSnapshot({
    ...draft,
    photos: photo ? [...draft.photos, photo] : draft.photos,
    captureInFlight: false,
    updatedAt: Date.now(),
  })

  if (!photo) return capture.status === "captured" ? "failed" : "nothing-to-do"
  // Saying "recovered" when the write was rejected would send the user back to
  // a form that does not have the photo, with no reason to suspect it.
  return saved ? "recovered" : "failed"
}

/**
 * Starts listening for a restored capture, and returns a promise that settles
 * once one has been handled or the grace period expires.
 *
 * Forms await {@link whenCaptureRecoverySettled} before reading their draft.
 * Without that, a form could load the draft, the recovery could then append
 * the photo, and the form's next autosave would write its stale snapshot
 * straight back over it.
 *
 * @returns an unsubscribe function, and the settled promise.
 */
export function startCaptureRecovery(
  onOutcome?: (outcome: RecoveryOutcome) => void,
): () => void {
  settled ??= new Promise<void>((resolve) => {
    markSettled = resolve
  })

  const timer = setTimeout(() => markSettled?.(), RECOVERY_GRACE_MS)

  const unsubscribe = photos.subscribeRestoredCapture((capture) => {
    // The platform layer reports arrival synchronously, with the file still
    // being read, precisely so this can happen now. The timeout means "nothing
    // is coming"; something has come, so it must not be left armed to open the
    // gate part-way through reading a full-resolution photo.
    clearTimeout(timer)

    void reattach(capture)
      .then((outcome) => onOutcome?.(outcome))
      .catch((error) => {
        console.warn("[Recovery] Could not reattach restored capture", error)
        onOutcome?.("failed")
      })
      .finally(() => markSettled?.())
  })

  return () => {
    clearTimeout(timer)
    markSettled?.()
    unsubscribe()
  }
}

/** Resolves once a restored capture has been handled, or nothing is coming. */
export function whenCaptureRecoverySettled(): Promise<void> {
  // Nothing started the recovery (the web build, or a test), so there is
  // nothing to wait for.
  return settled ?? Promise.resolve()
}

/** Test seam: forgets that recovery has run. */
export function resetCaptureRecoveryForTests() {
  settled = null
  markSettled = null
}
